// BHP-1038: Tailwind secured shell (reference screen #2). Wraps a page in the
// AppShell with a token-derived identity and a nav built from NavigationService
// — no Redux (the legacy secured Layout's userReducer/customerReducer are being
// retired). Applied to the /dashboard route only for now; the other authorized
// routes keep the legacy Layout until they are migrated (see
// docs/design-tokens.md phase-out plan).
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import NavigationService from '../../Services/NavigationService';
import MenuStructure from '../../Models/MenuStructure';
import { getCurrentUserFromToken } from '../../features/account/application/accountApi';
import CustomerSwitcher from '../../features/customer/ui/CustomerSwitcher';
import AppShell from './AppShell';
import SidebarStats from './SidebarStats';
import AccountMenu from './AccountMenu';
import { ChevronDownIcon, HomeIcon, NavIcon } from './icons';

function isVisible(item: MenuStructure): boolean {
  return item.active || item.showInMenu === true;
}

function useActive(to: string): boolean {
  const { pathname } = useLocation();
  return pathname === to || pathname.startsWith(`${to}/`);
}

function visibleChildren(item: MenuStructure): MenuStructure[] {
  return (item.children ?? []).filter(isVisible);
}

function NavLink({
  item,
  parent,
  isChild = false
}: {
  item: MenuStructure;
  parent: MenuStructure | null;
  isChild?: boolean;
}): JSX.Element {
  const to = NavigationService.generateRouteUrl(
    item.routeUrl,
    item.routeName,
    parent
  );
  const active = useActive(to);
  return (
    <Link
      to={to}
      className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm no-underline transition-colors ${
        active
          ? 'bg-teal-50 font-semibold text-teal-700'
          : 'text-ink hover:bg-slate-100'
      }`}
    >
      {!isChild && item.icon !== '' ? (
        <NavIcon name={item.icon} className="h-5 w-5 shrink-0 text-teal-600" />
      ) : null}
      <span>{item.routeName}</span>
    </Link>
  );
}

// BHP-1094: collapsible group in the design's teal style (teal-pill when active,
// chevron toggle). Children collapse/expand; auto-opens when its route is active.
function NavGroup({ item }: { item: MenuStructure }): JSX.Element {
  const kids = visibleChildren(item);
  const to = NavigationService.generateRouteUrl(
    item.routeUrl,
    item.routeName,
    null
  );
  const parentActive = useActive(to);
  const [open, setOpen] = useState(parentActive);
  useEffect(() => {
    if (parentActive) {
      setOpen(true);
    }
  }, [parentActive]);

  return (
    <div>
      <div
        className={`flex items-center rounded-lg ${parentActive ? 'bg-teal-50' : ''}`}
      >
        <Link
          to={to}
          className={`flex flex-1 items-center gap-3 px-3 py-2 text-sm no-underline ${
            parentActive ? 'font-semibold text-teal-700' : 'text-ink'
          }`}
        >
          {item.icon !== '' ? (
            <NavIcon
              name={item.icon}
              className="h-5 w-5 shrink-0 text-teal-600"
            />
          ) : null}
          <span>{item.routeName}</span>
        </Link>
        <button
          type="button"
          onClick={() => {
            setOpen((value) => !value);
          }}
          className="border-0 bg-transparent px-2 py-2 text-ink-muted hover:text-ink"
          aria-label={`Toggle ${item.routeName} submenu`}
        >
          <ChevronDownIcon
            className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>
      </div>
      {open ? (
        <div className="ml-8 mt-1 space-y-1">
          {kids.map((child) => (
            <NavLink key={child.routeName} item={child} parent={item} isChild />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function SecuredShell({
  title,
  parent,
  children
}: {
  title: string;
  /** Optional intermediate breadcrumb (e.g. the section overview). */
  parent?: { label: string; to: string };
  children: React.ReactNode;
}): JSX.Element {
  const user = getCurrentUserFromToken();
  const { pathname } = useLocation();
  // Memoized: generateMenuStructure allocates the full tree + calls
  // FeatureFlagService; only rebuild when the user or route changes.
  const menu = useMemo(
    () => (user !== null ? NavigationService.generateMenuStructure(user) : []),
    [user?.uuid, pathname]
  );

  const brand = (
    <span>
      <span className="font-bold">BizHost</span>{' '}
      <span className="text-ink-muted">- hosting platform</span>
    </span>
  );

  const sidebar = (
    <div>
      <div className="text-sm leading-tight">
        <div className="text-ink-muted">Welcome,</div>
        <div className="font-semibold">{user?.firstName ?? ''}</div>
        <div className="mt-3 text-ink-muted">
          <CustomerSwitcher />
        </div>
      </div>

      <div className="my-4 border-b border-black/10" />

      <SidebarStats />

      <div className="my-4 border-b border-black/10" />

      <nav className="space-y-1">
        {menu
          .filter(isVisible)
          .map((item) =>
            visibleChildren(item).length > 0 ? (
              <NavGroup key={item.routeName} item={item} />
            ) : (
              <NavLink key={item.routeName} item={item} parent={null} />
            )
          )}
      </nav>
    </div>
  );

  const topbar = (
    <AccountMenu
      name={user?.firstName ?? 'Account'}
      email={user?.email ?? null}
    />
  );

  const breadcrumb = (
    <span className="inline-flex items-center gap-2">
      <Link
        to="/dashboard"
        aria-label="Home"
        className="inline-flex items-center text-ink-muted no-underline hover:text-brand"
      >
        <HomeIcon className="h-4 w-4" />
      </Link>
      <span>/</span>
      {parent !== undefined ? (
        <>
          <Link
            to={parent.to}
            className="text-ink-muted no-underline hover:text-brand"
          >
            {parent.label}
          </Link>
          <span>/</span>
        </>
      ) : null}
      <span className="text-ink">{title}</span>
    </span>
  );

  return (
    <AppShell
      brand={brand}
      sidebar={sidebar}
      topbar={topbar}
      title={title}
      breadcrumb={breadcrumb}
    >
      {children}
    </AppShell>
  );
}
